import React from 'react'
import { Box } from '@mui/material'

const DEFAULT_OVERSCAN_VIEWPORTS = 0.5
const MIN_OVERSCAN_PX = 240

interface ViewportAheadCardProps {
  cardId: string
  estimatedHeight: number
  placeholder: React.ReactNode
  renderCard: () => React.ReactNode
  enabled?: boolean
}

const supportsViewportObservation = (): boolean =>
  typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function'

const ViewportAheadCard: React.FC<ViewportAheadCardProps> = ({
  cardId,
  estimatedHeight,
  placeholder,
  renderCard,
  enabled = true,
}) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const [mounted, setMounted] = React.useState(() => !enabled || !supportsViewportObservation())
  const [measuredHeight, setMeasuredHeight] = React.useState<number | null>(null)

  React.useLayoutEffect(() => {
    if (!enabled || !supportsViewportObservation()) {
      setMounted(true)
      return
    }

    const node = rootRef.current
    if (!node) {
      return
    }

    const overscan = Math.max(
      MIN_OVERSCAN_PX,
      Math.round(window.innerHeight * DEFAULT_OVERSCAN_VIEWPORTS),
    )
    const rect = node.getBoundingClientRect()
    if (rect.bottom >= -overscan && rect.top <= window.innerHeight + overscan) {
      setMounted(true)
      return
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) {
          return
        }
        if (entry.isIntersecting) {
          setMounted(true)
          observer.disconnect()
        }
      },
      { rootMargin: `${overscan}px 0px ${overscan}px 0px` },
    )

    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [enabled])

  React.useLayoutEffect(() => {
    if (!mounted || typeof window === 'undefined') {
      return
    }
    const node = rootRef.current
    if (!node) {
      return
    }

    const recordHeight = () => {
      const height = Math.ceil(node.getBoundingClientRect().height)
      if (height > 0) {
        setMeasuredHeight((current) => (current === height ? current : height))
      }
    }

    recordHeight()
    if (typeof window.ResizeObserver !== 'function') {
      return
    }

    const resizeObserver = new window.ResizeObserver((entries) => {
      const height = Math.ceil(entries[0]?.contentRect.height ?? 0)
      if (height > 0) {
        setMeasuredHeight((current) => (current === height ? current : height))
      }
    })
    resizeObserver.observe(node)
    return () => resizeObserver.disconnect()
  }, [mounted])

  const reservedHeight = measuredHeight ?? Math.max(52, Math.ceil(estimatedHeight))

  return (
    <Box
      ref={rootRef}
      data-prep-card-viewport={cardId}
      data-card-mounted={mounted ? 'true' : 'false'}
      data-reserved-height={reservedHeight}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: mounted ? undefined : `${reservedHeight}px`,
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${reservedHeight}px`,
      }}
    >
      {mounted ? renderCard() : placeholder}
    </Box>
  )
}

export default React.memo(ViewportAheadCard)
